import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import { COLORS, FONT_FAMILY, FONT_HEADING, appTheme } from '../talkTreeShared/Tree/constants/theme';
import SimulatorIllustration from '../assets/Illustration1.svg';
import UploadIllustration from '../assets/Illustration2.svg';
import StreamIllustration from '../assets/Illustration3.svg';

const OPTIONS = [
  {
    id: 'simulator',
    illustration: SimulatorIllustration,
    title: 'Quick Tree Simulator',
    description: 'Answer a few questions about your classroom discussion habits, and our simulator will generate a talk tree instantly.',
  },
  {
    id: 'upload',
    illustration: UploadIllustration,
    title: 'Upload a Recording',
    description: 'Already have a classroom discussion recorded? Upload a recording or transcript to generate a talk tree from real classroom talk.',
  },
  {
    id: 'stream',
    illustration: StreamIllustration,
    title: 'Stream Your Classroom Live',
    description: 'Stream into Talk Trees during a live classroom discussion to generate a Talk Tree.',
  },
];

const OPTION_ROUTES = {

  simulator: '/Dashboard#/simulator',
  upload: '/Dashboard#/upload',
  stream: '/Dashboard#/stream',
};

const LIBRARY_ROUTE = '/Dashboard#/library';
const FALLBACK_ROUTE = '/Dashboard#/library';

export default function WelcomeOnboarding() {
  const navigate = useNavigate();


  useEffect(() => { mixpanel.track('onboardingPageViewed'); }, []);


  const finish = (to) => {
    navigate(to, { replace: true });
  };

  const handleSelectOption = (optionId) => {
    mixpanel.track('onboardingOptionSelected', { option: optionId });
    finish(OPTION_ROUTES[optionId] || FALLBACK_ROUTE);
  };

  const handleExploreLibrary = () => {
    mixpanel.track('onboardingLibraryExplored');
    finish(LIBRARY_ROUTE);
  };

  return (
    <ThemeProvider theme={appTheme}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', minHeight: '100vh', backgroundColor: COLORS.page, fontFamily: FONT_FAMILY }}>
        {/* Left illustration panel — 40% on desktop, hidden below md */}
        <Box
          aria-hidden="true"
          sx={{
            width: { xs: 0, md: '40%' },
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            background: COLORS.panelGradient,
            position: 'sticky',
            top: 0,
            height: '100vh',
            px: { md: 4, lg: 8 },
          }}
        >
          <Box component="img" src='/SampleTree.png' alt="" sx={{ width: '100%', maxWidth: 420, display: 'block', objectFit: 'contain' }} />
        </Box>

        {/* Right content panel */}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            backgroundColor: COLORS.white,
            px: 3,
            py: 6,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, width: '100%', maxWidth: 560, minWidth: 0 }}>
            {/* Heading with inline TalkTrees mark — same construction as Login/SignUp */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 0.5 }}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 500,
                  fontSize: { xs: 26, sm: 32 },
                  letterSpacing: '0px',
                  color: COLORS.primary,
                  lineHeight: 1.1,
                }}
              >
                Welcome to
              </Typography>
              <Box component="img" src="/TTLogo.svg" alt="TalkTrees" sx={{ height: { xs: 30, sm: 38 }, width: 'auto', display: 'block', flexShrink: 0 }} />
            </Box>

            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: 16,
                lineHeight: 1.6,
                color: COLORS.text,
                textAlign: 'center',
              }}
            >
              Whether you're ready to create a Talk Tree or just looking around, we've got you covered. Choose an option below to get started, or explore sample trees in the Tree Library.      
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              {OPTIONS.map((option) => (
                <Box
                  key={option.id}
                  component="button"
                  type="button"
                  onClick={() => handleSelectOption(option.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    width: '100%',
                    p: 2.5,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '10px',
                    background: COLORS.panelGradient,
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    '&:hover': { borderColor: COLORS.primary, backgroundColor: COLORS.tintedHover },
                    '&:focus-visible': { outline: `2px solid ${COLORS.primary}`, outlineOffset: '2px' },
                  }}
                >
                  <Box
                    component="img"
                    src={option.illustration}
                    alt=""
                    sx={{ width: 88, height: 90, flexShrink: 0, objectFit: 'contain' }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT_HEADING,
                        fontSize: 20,
                        lineHeight: 1.3,
                        fontWeight: 500,
                        color: COLORS.text,
                      }}
                    >
                      {option.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: 15,
                        lineHeight: 1.5,
                        color: COLORS.textMuted,
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={handleExploreLibrary}
            sx={{
              background: 'none',
              border: 0,
              p: 0,
              cursor: 'pointer',
              fontFamily: FONT_FAMILY,
              fontSize: 15,
              fontWeight: 400,
              color: COLORS.primary,
              textDecoration: 'underline',
              '&:hover': { color: COLORS.primaryHover },
              '&:focus-visible': { outline: `2px solid ${COLORS.primary}`, outlineOffset: '2px' },
            }}
          >
            Explore sample trees in the Tree Library
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}